/**
 * Local editing state that follows the document without fighting the user.
 *
 * Every settings panel keeps a copy of the value it is showing, so that typing
 * or dragging feels immediate. The hard part is deciding when the document is
 * allowed to replace that copy — get it wrong and the field snaps back under the
 * user's hands, which is what both the Design and Custom Variables panels did.
 *
 * Shared by both, and by anything else that edits a value it does not own.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";

/**
 * Local editing state that follows the document without fighting the user.
 *
 * Three rules, all of them load-bearing:
 *
 * IDENTITY IS NOT CHANGE. A panel's values are rebuilt from the shared variable
 * store on every notification, so an object-valued field — a responsive pair, a
 * vec2, a JSON blob — receives a brand new object every time ANY variable
 * changes, its own save landing included. Re-syncing on identity threw the
 * in-progress edit away roughly every 200 ms, which is what made a number
 * impossible to drag: it snapped back to the last committed value.
 *
 * A GESTURE OWNS ITS FIELD. While the pointer is down or the field has focus,
 * the local value is the truth. An engine echo or a save acknowledgement that
 * lands mid-drag waits for the gesture to finish.
 *
 * AN UNCOMMITTED EDIT OWNS ITS FIELD TOO. Between a change and its debounced
 * commit the document still holds the old value; adopting it would undo what was
 * just typed. This mirrors the document store's rule that a locally written path
 * is never overwritten from outside until it has been acknowledged.
 *
 * @param {*} value the committed value from the document
 * @param {boolean} [isEditing] true while a gesture or focus owns the field
 * @returns {{ value: *, ref: object, set: Function, release: Function }}
 */
export function useEditableValue(value, isEditing = false) {
  const [local, setLocal] = useState(value);
  const localRef = useRef(value);
  const pendingRef = useRef(false);

  const set = useCallback((next) => {
    localRef.current = next;
    pendingRef.current = true;
    setLocal(next);
  }, []);

  /** Give the field back to the document — the edit has been committed or dropped. */
  const release = useCallback(() => {
    pendingRef.current = false;
  }, []);

  useEffect(() => {
    if (_.isEqual(localRef.current, value)) {
      // The document caught up with us: whatever was owed has landed.
      pendingRef.current = false;
      return;
    }
    if (isEditing || pendingRef.current) return;
    localRef.current = value;
    setLocal(value);
  }, [value, isEditing]);

  return { value: local, ref: localRef, set, release };
}
